
import {
  CFormTextarea,
  CFormSelect,
  CFormCheck,
  CButton,
  CRow,
  CCol,
} from "@coreui/react";
import { useFormContext } from "react-hook-form";
import { Field, PickupLocationMap, SectionCard } from "../__sections";
import { GeoMode, LatLng, PlacePoint } from "../../../helpers/enums";
import { VEHICLE_TYPES } from "../../../helpers/constants";
import { colors } from "../../../config/theme";

interface Step2Props {
  Cities: City[];
  pickupCityValue: string;
  setPickupCityValue: (v: string) => void;
  pickupGeoMode: GeoMode;
  setPickupGeoMode: (m: GeoMode) => void;
  pickupPlaces: PlacePoint[];
  addPickupPlace: (lat: number, lng: number, name?: string) => void;
  removePickupPlace: (id: string) => void;
  pickupPolygon: LatLng[];
  onPickupPolygonChange: (coords: LatLng[]) => void;
  pickupCity: LatLng | null;

  dropoffSameAsPickup: boolean;
  setDropoffSameAsPickup: (v: boolean) => void;
  dropoffCityValue: string;
  setDropoffCityValue: (v: string) => void;
  dropoffGeoMode: GeoMode;
  setDropoffGeoMode: (m: GeoMode) => void;
  dropoffPlaces: PlacePoint[];
  addDropoffPlace: (lat: number, lng: number, name?: string) => void;
  removeDropoffPlace: (id: string) => void;
  dropoffPolygon: LatLng[];
  onDropoffPolygonChange: (coords: LatLng[]) => void;
  dropoffCity: LatLng | null;
}

export default function PickupDropoff(props: Step2Props) {
  const { register, watch } = useFormContext();
  const watchedPickupDropoff = watch("pickupDropoff");

  return (
    <div className="animate-fade-in">
      <SectionCard
        title="Pickup"
        subtitle="Choisissez un ou plusieurs points de rendez-vous, ou dessinez une zone de prise en charge."
      >
        <CFormCheck
          id="pickupAvailable"
          label="Pickup disponible"
          {...register("pickupDropoff.isPickupAvailable")}
        />

        {watchedPickupDropoff?.isPickupAvailable && (
          <div className="mt-3 ps-1">
            <CRow>
              <CCol md={6}>
                <Field label="Ville (pour centrer la carte)">
                  <CFormSelect
                    value={props.pickupCityValue}
                    onChange={(e) => props.setPickupCityValue(e.target.value)}
                  >
                    <option value="">Sélectionner une ville...</option>
                    {props.Cities.map((c) => (
                      <option key={c.label} value={c.label}>
                        {c.label}
                      </option>
                    ))}
                  </CFormSelect>
                </Field>
              </CCol>
              <CCol md={6}>
                <Field label="Type de véhicule">
                  <CFormSelect {...register("pickupDropoff.transportation")}>
                    <option value="">Sélectionner...</option>
                    {VEHICLE_TYPES.map((v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </CFormSelect>
                </Field>
              </CCol>
            </CRow>

            <div className="mt-3">
              <Field label="Description / instructions">
                <CFormTextarea
                  rows={2}
                  {...register("pickupDropoff.pickupDescription")}
                  placeholder="ex: devant l'hôtel, à l'accueil..."
                />
              </Field>
            </div>

            <div className="d-flex gap-2 mt-3 mb-2">
              <CButton
                size="sm"
                onClick={() => props.setPickupGeoMode("points")}
                style={{
                  backgroundColor:
                    props.pickupGeoMode === "points"
                      ? colors.yellow
                      : "#f4f4f4",
                  color: props.pickupGeoMode === "points" ? "#fff" : "#333",
                  border: "none",
                }}
              >
                Points multiples
              </CButton>
              <CButton
                size="sm"
                onClick={() => props.setPickupGeoMode("zones")}
                style={{
                  backgroundColor:
                    props.pickupGeoMode === "zones" ? colors.yellow : "#f4f4f4",
                  color: props.pickupGeoMode === "zones" ? "#fff" : "#333",
                  border: "none",
                }}
              >
                Zone sur la carte
              </CButton>
            </div>

            <PickupLocationMap
              city={props.pickupCity}
              mode={props.pickupGeoMode}
              places={props.pickupPlaces}
              onAddPlace={props.addPickupPlace}
              onRemovePlace={props.removePickupPlace}
              polygon={props.pickupPolygon}
              onPolygonChange={props.onPickupPolygonChange}
            />
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Dropoff"
        subtitle="Précisez le point ou la zone de dépose, ou réutilisez les mêmes données que le pickup."
      >
        <CFormCheck
          id="dropoffAvailable"
          label="Dropoff disponible"
          {...register("pickupDropoff.isDropoffAvailable")}
        />

        {watchedPickupDropoff?.isDropoffAvailable && (
          <div className="mt-3 ps-1">
            <CFormCheck
              id="dropoffSameAsPickup"
              label="Identique au pickup — ne pas ressaisir"
              checked={props.dropoffSameAsPickup}
              onChange={(e) => props.setDropoffSameAsPickup(e.target.checked)}
            />

            {!props.dropoffSameAsPickup && (
              <div className="mt-3">
                <CRow>
                  <CCol md={6}>
                    <Field label="Ville (pour centrer la carte)">
                      <CFormSelect
                        value={props.dropoffCityValue}
                        onChange={(e) =>
                          props.setDropoffCityValue(e.target.value)
                        }
                      >
                        <option value="">Sélectionner une ville...</option>
                        {props.Cities.map((c) => (
                          <option key={c.label} value={c.label}>
                            {c.label}
                          </option>
                        ))}
                      </CFormSelect>
                    </Field>
                  </CCol>
                  <CCol md={6}>
                    <Field label="Description / instructions">
                      <CFormTextarea
                        rows={2}
                        {...register("pickupDropoff.dropoffDescription")}
                        placeholder="ex: devant l'hôtel, à l'accueil..."
                      />
                    </Field>
                  </CCol>
                </CRow>

                <div className="d-flex gap-2 mt-3 mb-2">
                  <CButton
                    size="sm"
                    onClick={() => props.setDropoffGeoMode("points")}
                    style={{
                      backgroundColor:
                        props.dropoffGeoMode === "points"
                          ? colors.yellow
                          : "#f4f4f4",
                      color:
                        props.dropoffGeoMode === "points" ? "#fff" : "#333",
                      border: "none",
                    }}
                  >
                    Points multiples
                  </CButton>
                  <CButton
                    size="sm"
                    onClick={() => props.setDropoffGeoMode("zones")}
                    style={{
                      backgroundColor:
                        props.dropoffGeoMode === "zones"
                          ? colors.yellow
                          : "#f4f4f4",
                      color: props.dropoffGeoMode === "zones" ? "#fff" : "#333",
                      border: "none",
                    }}
                  >
                    Zone sur la carte
                  </CButton>
                </div>

                <PickupLocationMap
                  city={props.dropoffCity}
                  mode={props.dropoffGeoMode}
                  places={props.dropoffPlaces}
                  onAddPlace={props.addDropoffPlace}
                  onRemovePlace={props.removeDropoffPlace}
                  polygon={props.dropoffPolygon}
                  onPolygonChange={props.onDropoffPolygonChange}
                />
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
